import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DragIndicator } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";
import useSurvey from "../../hooks/useSurvey";
import usePage from "../../hooks/usePage";

import Page from "./Page";
import SurveyPage from "../ui/SurveyPage";
import SurveySection from "../ui/SurveySection";
import SectionQuestion from "../ui/SectionQuestion";
import HTMLDisplay from "../ui/HTMLDisplay";
import InfoBox from "../layout/InfoBox";
import Switch from "../elements/Switch";

import SurveyPageModal from "../modals/SurveyPageModal";
import SurveySectionModal from "../modals/SurveySectionModal";
import SurveyQuestionModal from "../modals/SurveyQuestionModal";
import SurveyEntryModal from "../modals/SurveyEntryModal";
import SurveyCopyModal from "../modals/SurveyCopyModal";

const SurveyDesigner = () => {
  const theme = useTheme();
  const { getLabel, getTitle, getMessage } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const { pageBody } = useBrowser();
  const { getFromServer, sortByFields, executeAction, updateRecord } =
    useData();
  const { validateQuestionMove, validateSectionMove, validatePageMove } =
    useSurvey();
  const { showMessage } = usePage();

  const [surveyId, setSurveyId] = useState();
  const [projectId, setProjectId] = useState();
  const [clientId, setClientId] = useState();

  const [survey, setSurvey] = useState(null);
  const [pages, setPages] = useState(null);
  const [sections, setSections] = useState(null);
  const [questions, setQuestions] = useState(null);

  const [pageExpanded, setPageExpanded] = useState(null);
  const [sectionExpanded, setSectionExpanded] = useState(null);
  const [questionExpanded, setQuestionExpanded] = useState(null);

  const [openPageDialog, setOpenPageDialog] = useState(false);
  const [openSectionDialog, setOpenSectionDialog] = useState(false);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);

  const [currentPage, setCurrentPage] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  const [openSurveyEntry, setOpenSurveyEntry] = useState(false);
  const [openSurveyCopy, setOpenSurveyCopy] = useState(false);

  const [pageDropHoverIndex, setPageDropHoverIndex] = useState(null);
  const [sectionDropHoverIndex, setSectionDropHoverIndex] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [hasCallResults, setHasCallResults] = useState(false);

  useEffect(() => {
    setSurveyId(location.state.surveyId);
    setProjectId(location.state.projectId);
    setClientId(location.state.clientId);
  }, []);

  useEffect(() => {
    if (!surveyId) return;

    getData();
  }, [surveyId]);

  useEffect(() => {
    if (!surveyId) return;

    setPageExpanded(null);
    setSectionExpanded(null);
    setQuestionExpanded(null);
    setCurrentPage(null);
    setCurrentSection(null);
    setCurrentQuestion(null);
    setSections(null);
    setQuestions(null);

    getData();
  }, [includeInactive]);

  const filterByActive = (list) => {
    if (!Array.isArray(list)) return [];
    if (includeInactive) return list;
    return list.filter((item) => item?.isActive !== false);
  };

  const getData = async (args) => {
    if (!surveyId) return;

    const surveyData = await getFromServer({
      entityName: "Survey",
      id: surveyId,
    });
    setSurvey(surveyData);

    const callResultCountResults = await executeAction({
      entityName: "CallResult",
      action: "getRecordCount",
      filter: { surveyId },
    });
    const callResultCount = callResultCountResults?.data?.recordCount || 0;
    setHasCallResults(callResultCount > 0);

    let pageList = await getFromServer({
      entityName: "SurveyPage",
      filter: { surveyId },
    });

    if (Array.isArray(pageList)) {
      pageList = sortByFields(pageList, ["pageNumber"]);
      pageList = filterByActive(pageList);
      setPages(pageList);
    } else {
      setPages([]);
    }

    // Use fresh locals to avoid stale state while refetching
    const activePage =
      pageExpanded !== null && Array.isArray(pageList)
        ? pageList[pageExpanded]
        : null;

    let sectionList = [];
    if (activePage) {
      setCurrentPage(activePage);
      sectionList = await getPageSections(activePage.id);
    }

    const activeSection =
      sectionExpanded !== null && Array.isArray(sectionList)
        ? sectionList[sectionExpanded]
        : currentSection;

    let questionsForSection = [];

    if (activeSection) {
      setCurrentSection(activeSection);
      questionsForSection = await getSectionQuestions(activeSection.id);
    }

    if (args && args.refresh) {
      if (args.refresh === "surveyQuestion") {
        setCurrentQuestion(questionsForSection[questionsForSection.length - 1]);
      }
    }
  };

  const handleToolbarClick = (itemClicked) => {
    switch (itemClicked) {
      case "survey":
        navigate("/survey", { state: { surveyId, projectId, clientId } });
        break;
      case "newPage":
        setCurrentPage(null);
        setCurrentSection(null);
        setCurrentQuestion(null);

        setPageExpanded(null);
        setSectionExpanded(null);
        setQuestionExpanded(null);

        setOpenPageDialog(true);
        break;
      case "newSection":
        setCurrentSection(null);
        setCurrentQuestion(null);

        setSectionExpanded(null);
        setQuestionExpanded(null);

        setOpenSectionDialog(true);
        break;
      case "newQuestion":
        setCurrentQuestion(null);

        setQuestionExpanded(null);

        setOpenQuestionDialog(true);
        break;
      case "copy":
        setOpenSurveyCopy(true);
        break;
      case "mobile":
        setOpenSurveyEntry(true);
        break;
    }
  };

  const handleDragPageStart = (e, index) => {
    e.dataTransfer.setData("dragType", "page");
    e.dataTransfer.setData("dragIndex", index);
  };

  const handlePageDrop = async (e, dropIndex) => {
    const dragType = e.dataTransfer.getData("dragType");
    if (dragType !== "page") return;

    const dragIndex = e.dataTransfer.getData("dragIndex");

    const draggedPage = pages[dragIndex];

    const allowedToMove = validatePageMove(draggedPage);
    if (!allowedToMove) {
      showMessage(getMessage("notAllowedToMove"), "error");
      return;
    }

    const updatedItems = [...pages];
    const [draggedItem] = updatedItems.splice(dragIndex, 1);
    updatedItems.splice(dropIndex, 0, draggedItem);

    await executeAction({
      entityName: "SurveyPage",
      action: "resequencePages",
      data: {
        pages: updatedItems,
      },
      displayMessage: true,
    });

    await getData();
  };

  const handlePageExpand = async (pageIndex, expanded) => {
    if (!expanded) {
      setPageExpanded(null);
      setSectionExpanded(null);
      setQuestionExpanded(null);

      setCurrentPage(null);
      setCurrentSection(null);
      setCurrentQuestion(null);

      setSections(null);
      setQuestions(null);
    } else {
      setPageExpanded(pageIndex);
      setCurrentPage(pages[pageIndex]);

      await getPageSections(pages[pageIndex].id);

      setSectionExpanded(null);
      setCurrentSection(null);

      setQuestionExpanded(null);
      setCurrentQuestion(null);
    }
  };

  const getPageSections = async (surveyPageId) => {
    let sectionList = await getFromServer({
      entityName: "SurveySection",
      filter: { surveyPageId },
    });

    sectionList = filterByActive(sectionList);
    sectionList = sortByFields(sectionList, ["sectionNumber"]);
    setSections(sectionList);

    if (sectionExpanded !== null) {
      setCurrentSection(sectionList[sectionExpanded]);
    }

    return sectionList;
  };

  const handlePageEdit = () => {
    setOpenPageDialog(true);
  };

  const handlePageClose = async (refresh) => {
    setOpenPageDialog(false);
    if (refresh) {
      await getData();
    }
  };

  // SECTIONS
  const handleDragSectionStart = (e, index) => {
    e.dataTransfer.setData("dragType", "section");
    e.dataTransfer.setData("dragIndex", index);
  };

  const handleSectionDrop = async (e, dropIndex) => {
    const dragType = e.dataTransfer.getData("dragType");
    if (dragType !== "section") return;

    const dragIndex = e.dataTransfer.getData("dragIndex");

    const draggedSectoin = sections[dragIndex];

    const allowedToMove = validateSectionMove(draggedSectoin);
    if (!allowedToMove) {
      showMessage(getMessage("notAllowedToMove"), "error");
      return;
    }

    const updatedItems = [...sections];
    const [draggedItem] = updatedItems.splice(dragIndex, 1);
    updatedItems.splice(dropIndex, 0, draggedItem);

    await executeAction({
      entityName: "SurveySection",
      action: "resequenceSections",
      data: {
        surveyPageId: currentPage.id,
        sections: updatedItems,
      },
      displayMessage: true,
    });

    await getData();
  };

  const handleMoveSection = async (e, pageIndex) => {
    const dragType = e.dataTransfer.getData("dragType");
    if (dragType !== "section") return;

    const dragIndex = e.dataTransfer.getData("dragIndex");

    const movedSection = sections[dragIndex];
    if (!movedSection) return;

    const allowedToMove = validateSectionMove(movedSection);
    if (!allowedToMove) {
      showMessage(getMessage("notAllowedToMove"), "error");
      return;
    }

    const toSurveyPageId = pages[pageIndex].id;
    if (!toSurveyPageId || toSurveyPageId === currentPage.id) return;

    const pageSections = await getFromServer({
      entityName: "SurveySection",
      filter: { surveyPageId: toSurveyPageId },
    });

    const updatedSection = {
      ...movedSection,
      surveyPageId: toSurveyPageId,
      sectionNumber: Array.isArray(pageSections) ? pageSections.length : 0,
    };

    await updateRecord("SurveySection", updatedSection, false);

    let sourceSections = await getFromServer({
      entityName: "SurveySection",
      filter: { surveyPageId: currentPage.id },
    });
    sourceSections = sortByFields(sourceSections, ["sectionNumber"]);

    let destinationSections = await getFromServer({
      entityName: "SurveySection",
      filter: { surveyPageId: toSurveyPageId },
    });
    destinationSections = sortByFields(destinationSections, ["sectionNumber"]);

    await executeAction({
      entityName: "SurveySection",
      action: "resequenceSections",
      data: {
        surveyPageId: currentPage.id,
        sections: sourceSections,
      },
    });

    await executeAction({
      entityName: "SurveySection",
      action: "resequenceSections",
      data: {
        surveyPageId: toSurveyPageId,
        sections: destinationSections,
      },
    });

    await getData();
  };

  const handleSectionExpand = async (sectionIndex, expanded) => {
    if (!expanded) {
      setSectionExpanded(null);
      setCurrentSection(null);

      setQuestionExpanded(null);
      setCurrentQuestion(null);
    } else {
      setSectionExpanded(sectionIndex);
      setCurrentSection(sections[sectionIndex]);

      await getSectionQuestions(sections[sectionIndex].id);

      setQuestionExpanded(null);
      setCurrentQuestion(null);
    }
  };

  const getSectionQuestions = async (surveySectionId) => {
    let sectionQuestions = await getFromServer({
      entityName: "SurveyQuestion",
      filter: { surveySectionId },
    });

    sectionQuestions = filterByActive(sectionQuestions);
    sectionQuestions = sortByFields(sectionQuestions, ["questionNumber"]);

    setQuestions(sectionQuestions);

    if (questionExpanded !== null) {
      setCurrentQuestion(sectionQuestions[questionExpanded]);
    }

    return sectionQuestions;
  };

  const handleSectionEdit = (sectionIndex) => {
    setSectionExpanded(sectionIndex);
    setCurrentSection(sections[sectionIndex]);
    setOpenSectionDialog(true);
  };

  const handleSectionDelete = async () => {
    setSectionExpanded(null);
    setCurrentSection(null);
    setOpenSectionDialog(false);
    await getData();
  };

  const handleSectionClose = async (refresh) => {
    setOpenSectionDialog(false);
    if (refresh) {
      await getData();
    }
  };

  const handleSectionSave = async () => {
    setOpenSectionDialog(false);
    await getData();
  };

  // QUESTIONS
  const handleDragQuestionStart = async (e, index) => {
    e.dataTransfer.setData("dragType", "question");
    e.dataTransfer.setData("dragIndex", index);
  };

  const handleQuestionDrop = async (e, dropIndex) => {
    const dragType = e.dataTransfer.getData("dragType");
    if (dragType !== "question") return;

    const dragIndex = e.dataTransfer.getData("dragIndex");

    const draggedQuestion = questions[dragIndex];

    const allowedToMove = await validateQuestionMove(draggedQuestion);
    if (!allowedToMove) {
      showMessage(getMessage("notAllowedToMove"), "error");
      return;
    }

    const updatedItems = [...questions];
    const [draggedItem] = updatedItems.splice(dragIndex, 1);
    updatedItems.splice(dropIndex, 0, draggedItem);

    await executeAction({
      entityName: "SurveyQuestion",
      action: "resequenceQuestions",
      data: {
        questions: updatedItems,
      },
      displayMessage: true,
    });

    await getData();
  };

  const handleMoveQuestion = async (e, sectionIndex) => {
    const dragType = e.dataTransfer.getData("dragType");
    if (dragType !== "question") return;

    const dragIndex = e.dataTransfer.getData("dragIndex");

    const movedQuestion = questions[dragIndex];
    if (!movedQuestion) return;

    const allowedToMove = await validateQuestionMove(movedQuestion);
    if (!allowedToMove) {
      showMessage(getMessage("notAllowedToMove"), "error");
      return;
    }

    const toSection = sections[sectionIndex];
    if (!toSection || toSection.id === currentSection.id) return;

    let destinationQuestions = await getFromServer({
      entityName: "SurveyQuestion",
      filter: { surveySectionId: toSection.id },
    });

    const updatedQuestion = {
      ...movedQuestion,
      surveySectionId: toSection.id,
      questionNumber: Array.isArray(destinationQuestions)
        ? destinationQuestions.length
        : 0,
    };

    await updateRecord("SurveyQuestion", updatedQuestion, false);

    let sourceQuestions = await getFromServer({
      entityName: "SurveyQuestion",
      filter: { surveySectionId: currentSection.id },
    });
    sourceQuestions = sortByFields(sourceQuestions, ["questionNumber"]);

    destinationQuestions = await getFromServer({
      entityName: "SurveyQuestion",
      filter: { surveySectionId: toSection.id },
    });
    destinationQuestions = sortByFields(destinationQuestions, [
      "questionNumber",
    ]);

    await executeAction({
      entityName: "SurveyQuestion",
      action: "resequenceQuestions",
      data: {
        questions: sourceQuestions,
      },
    });

    await executeAction({
      entityName: "SurveyQuestion",
      action: "resequenceQuestions",
      data: {
        questions: destinationQuestions,
      },
    });

    await getData();
  };

  const handleQuestionExpand = async (questionIndex, expanded) => {
    if (!expanded) {
      setQuestionExpanded(null);
      setCurrentQuestion(null);
    } else {
      setQuestionExpanded(questionIndex);
      setCurrentQuestion(questions[questionIndex]);
    }
  };

  const handleQuestionEdit = () => {
    setOpenQuestionDialog(true);
  };

  const handleQuestionDelete = async () => {
    setQuestionExpanded(null);
    setCurrentQuestion(null);
    setOpenQuestionDialog(false);
    await getData();
  };

  const handleQuestionClose = async (refresh) => {
    setOpenQuestionDialog(false);
    if (refresh) {
      await getData();
    }
  };

  const hideQuestionIcon = () => {
    if (!currentSection) return true;
    if (currentSection.sectionType === "text") return true;

    return false;
  };

  const handleSurveyCopy = async () => {
    setOpenSurveyCopy(false);
    await getData();
  };

  const handleRefreshQuestion = async () => {
    await getData({ refresh: "surveyQuestion" });
  };

  return (
    <>
      <Page
        pageName={getTitle("surveyDesigner")}
        includeToolbar
        items={[
          { name: "survey", iconName: "Article" },
          { name: "newPage", iconName: "NoteAdd" },
          {
            name: "newSection",
            iconName: "PostAdd",
            hide: pageExpanded !== null ? false : true,
          },
          {
            name: "newQuestion",
            iconName: "Quiz",
            hide: hideQuestionIcon(),
          },
          {
            name: "copy",
            iconName: "ContentCopy",
          },
          {
            name: "mobile",
            iconName: "Smartphone",
          },
        ]}
        onToolItemClick={handleToolbarClick}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            paddingBottom: "8px",
          }}
        >
          <Switch
            label="Include inactive"
            value={includeInactive}
            onChange={(newValue) => setIncludeInactive(Boolean(newValue))}
          />
        </Box>

        {!Array.isArray(pages) || pages.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
          >
            <Typography>{getMessage("surveyNotStarted")}</Typography>
          </Box>
        ) : (
          <Box
            display="flex"
            gap={1}
          >
            <Box
              display="flex"
              flexDirection="column"
              sx={{ height: `${pageBody.height}px`, overflow: "auto", flex: 1 }}
            >
              {pages.map((page, index) => (
                <Box
                  display="flex"
                  key={`page_${index}`}
                >
                  {pageExpanded === null ? (
                    <Box
                      draggable
                      onDragStart={(e) => handleDragPageStart(e, index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handlePageDrop(e, index)}
                      sx={{
                        marginTop: "10px",
                      }}
                    >
                      <DragIndicator sx={{ cursor: "grab" }} />
                    </Box>
                  ) : null}
                  <Box
                    onDrop={(e) => {
                      setPageDropHoverIndex(null);
                      handleMoveSection(e, index);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => {
                      if (e.dataTransfer.getData("dragType") === "section") {
                        setPageDropHoverIndex(index);
                      }
                    }}
                    onDragLeave={() => {
                      if (pageDropHoverIndex === index) {
                        setPageDropHoverIndex(null);
                      }
                    }}
                    sx={{
                      border:
                        pageDropHoverIndex === index
                          ? `2px dashed ${theme.palette.primary.main}`
                          : "2px dashed transparent",
                      borderRadius: "6px",
                      transition: "border-color 120ms ease",
                    }}
                  >
                    <SurveyPage
                      surveyId={surveyId}
                      page={page}
                      pageIndex={index}
                      expanded={pageExpanded === index ? true : false}
                      onExpand={handlePageExpand}
                      onEdit={handlePageEdit}
                    />
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ flex: 1 }}>
              {pageExpanded !== null ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ height: `${pageBody.height}px`, overflow: "auto" }}
                >
                  {Array.isArray(sections) && sections.length > 0 ? (
                    sections.map((section, index) => (
                      <Box
                        key={`section_${index}`}
                        display="flex"
                      >
                        {sectionExpanded === null ? (
                          <Box
                            draggable
                            onDragStart={(e) =>
                              handleDragSectionStart(e, index)
                            }
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleSectionDrop(e, index)}
                            sx={{
                              marginTop: "10px",
                            }}
                          >
                            <DragIndicator sx={{ cursor: "grab" }} />
                          </Box>
                        ) : null}
                        <Box
                          onDrop={(e) => {
                            setSectionDropHoverIndex(null);
                            handleMoveQuestion(e, index);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          onDragEnter={(e) => {
                            if (
                              e.dataTransfer.getData("dragType") === "question"
                            ) {
                              setSectionDropHoverIndex(index);
                            }
                          }}
                          onDragLeave={() => {
                            if (sectionDropHoverIndex === index) {
                              setSectionDropHoverIndex(null);
                            }
                          }}
                          sx={{
                            border:
                              sectionDropHoverIndex === index
                                ? `2px dashed ${theme.palette.primary.main}`
                                : "2px dashed transparent",
                            borderRadius: "6px",
                            transition: "border-color 120ms ease",
                          }}
                        >
                          <SurveySection
                            surveyId={surveyId}
                            pageIndex={pageExpanded}
                            pageId={currentPage.id}
                            section={section}
                            sectionIndex={index}
                            expanded={sectionExpanded === index ? true : false}
                            onExpand={handleSectionExpand}
                            onEdit={handleSectionEdit}
                          />
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      width="300px"
                    >
                      <Typography>{getMessage("noSections")}</Typography>
                    </Box>
                  )}
                </Box>
              ) : null}
            </Box>

            <Box sx={{ flex: 1 }}>
              {sectionExpanded !== null &&
              currentSection.sectionType !== "text" ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ height: `${pageBody.height - 20}px`, overflow: "auto" }}
                >
                  {Array.isArray(questions) && questions.length > 0 ? (
                    questions.map((question, index) => (
                      <Box
                        key={`question_${index}`}
                        display="flex"
                      >
                        {questionExpanded === null ? (
                          <Box
                            draggable
                            onDragStart={(e) =>
                              handleDragQuestionStart(e, index)
                            }
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleQuestionDrop(e, index)}
                            sx={{
                              marginTop: "10px",
                            }}
                          >
                            <DragIndicator sx={{ cursor: "grab" }} />
                          </Box>
                        ) : null}
                        <SectionQuestion
                          surveyId={surveyId}
                          pageIndex={pageExpanded}
                          sectionIndex={sectionExpanded}
                          questionIndex={index}
                          question={question}
                          expanded={questionExpanded === index ? true : false}
                          onExpand={handleQuestionExpand}
                          onEdit={handleQuestionEdit}
                        />
                      </Box>
                    ))
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      width="300px"
                    >
                      <Typography>{getMessage("noQuestions")}</Typography>
                    </Box>
                  )}
                </Box>
              ) : null}
              {currentSection && currentSection.sectionType === "text" ? (
                <Box sx={{ width: "400px" }}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Section Text (English)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          padding: "8px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          backgroundColor: "#fafafa",
                          minHeight: "60px",
                          "& p": { margin: "0 0 8px 0" },
                        }}
                      >
                        <HTMLDisplay
                          html={
                            currentSection.text || "<em>No English text</em>"
                          }
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Section Text (French)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          padding: "8px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          backgroundColor: "#fafafa",
                          minHeight: "60px",
                          "& p": { margin: "0 0 8px 0" },
                        }}
                      >
                        <HTMLDisplay
                          html={
                            currentSection.text_fr || "<em>No French text</em>"
                          }
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              ) : null}
            </Box>
          </Box>
        )}
      </Page>

      {openPageDialog ? (
        <SurveyPageModal
          open={openPageDialog}
          onClose={() => handlePageClose(false)}
          surveyId={survey.id}
          page={currentPage}
          onSave={() => handlePageClose(true)}
          canDelete={!hasCallResults}
        />
      ) : null}

      {openSectionDialog ? (
        <SurveySectionModal
          open={openSectionDialog}
          onClose={() => handleSectionClose(false)}
          surveyId={survey.id}
          surveyPageId={currentPage.id}
          section={currentSection}
          onSave={handleSectionSave}
          onDelete={handleSectionDelete}
          canDelete={!hasCallResults}
        />
      ) : null}

      {openQuestionDialog ? (
        <SurveyQuestionModal
          open={openQuestionDialog}
          onClose={() => handleQuestionClose(false)}
          surveyId={survey.id}
          surveyPageId={currentPage.id}
          surveySectionId={currentSection.id}
          question={currentQuestion}
          onSave={() => handleQuestionClose(true)}
          onDelete={handleQuestionDelete}
          onRefresh={handleRefreshQuestion}
          canDelete={!hasCallResults}
        />
      ) : null}

      {openSurveyEntry ? (
        <SurveyEntryModal
          open={openSurveyEntry}
          onClose={() => setOpenSurveyEntry(false)}
          surveyId={survey.id}
          mode="test"
          layout="mobile"
        />
      ) : null}

      {openSurveyCopy ? (
        <SurveyCopyModal
          open={openSurveyCopy}
          onClose={() => setOpenSurveyCopy(false)}
          surveyId={survey.id}
          onSave={handleSurveyCopy}
        />
      ) : null}
    </>
  );
};

export default SurveyDesigner;
